import { SyntheticEvent, useEffect, useState } from 'react'

import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import Card from '@mui/material/Card'
import CardContent from '@mui/material/CardContent'
import CircularProgress from '@mui/material/CircularProgress'
import Divider from '@mui/material/Divider'
import Fab from '@mui/material/Fab'
import FormControl from '@mui/material/FormControl'
import Grid from '@mui/material/Grid'
import IconButton from '@mui/material/IconButton'
import Stack from '@mui/material/Stack'
import TextField from '@mui/material/TextField'
import Tooltip from '@mui/material/Tooltip'
import Typography from '@mui/material/Typography'

import Tab from '@mui/material/Tab'
import TabContext from '@mui/lab/TabContext'
import TabList from '@mui/lab/TabList'
import TabPanel from '@mui/lab/TabPanel'

import { useNavigate } from 'react-router-dom'
import axios from 'axios'
import Swal from 'sweetalert2'
import moment from 'moment'

import Icon from '../../../components/icon'
import apiUrl from '../../../configs/constant'
import MsiDatagrid from '../../../components/custom/MsiDatagridAuto'

const defaultTanggalAwal = moment()
    .subtract(3, 'months')
    .startOf('month')
    .format('YYYY-MM-DD')

const defaultTanggalAkhir = moment()
    .endOf('month')
    .format('YYYY-MM-DD')

const getErrorMessage = (
    error: unknown,
    fallback = 'Terjadi kesalahan pada server.'
): string => {
    if (axios.isAxiosError(error)) {
        return (
            error.response?.data?.message ||
            error.message ||
            fallback
        )
    }

    if (error instanceof Error) {
        return error.message || fallback
    }

    return fallback
}

const readBlobError = async (
    blob: Blob,
    fallback: string
): Promise<string> => {
    try {
        const text = await blob.text()

        if (!text) return fallback

        const payload = JSON.parse(text)

        return payload?.message || fallback
    } catch {
        return fallback
    }
}

export default function Index() {
    const navigate = useNavigate()

    const userData = JSON.parse(
        localStorage.getItem('user') || '{}'
    )
    const token = String(userData?.token || '')

    const [tabs, setTabs] = useState<string>('0')
    const [tglAwal, setTglAwal] = useState<string>(
        defaultTanggalAwal
    )
    const [tglAkhir, setTglAkhir] = useState<string>(
        defaultTanggalAkhir
    )
    const [listData, setListData] = useState<any[]>([])
    const [loading, setLoading] = useState<boolean>(false)
    const [deletingId, setDeletingId] = useState<string>('')
    const [printingId, setPrintingId] = useState<string>('')

    const getData = async (mode: string) => {
        if (!tglAwal || !tglAkhir) {
            await Swal.fire({
                title: 'Pemberitahuan',
                text: 'Tanggal awal dan tanggal akhir wajib diisi.',
                icon: 'warning'
            })

            return
        }

        if (tglAwal > tglAkhir) {
            await Swal.fire({
                title: 'Pemberitahuan',
                text: 'Tanggal awal tidak boleh melebihi tanggal akhir.',
                icon: 'warning'
            })

            return
        }

        setLoading(true)
        setListData([])

        try {
            const response = await axios.get(
                `${apiUrl.apiUrl}/production/bhp/list`,
                {
                    params: {
                        awal: tglAwal,
                        akhir: tglAkhir,
                        mode
                    },
                    headers: {
                        Accept: 'application/json',
                        Authorization: `Bearer ${token}`
                    }
                }
            )

            if (!response.data?.success) {
                throw new Error(
                    response.data?.message ||
                    'Data BHP gagal diambil.'
                )
            }

            setListData(
                Array.isArray(response.data?.data)
                    ? response.data.data
                    : []
            )
        } catch (error) {
            await Swal.fire({
                title: 'Pemberitahuan',
                text: getErrorMessage(
                    error,
                    'Data BHP gagal diambil.'
                ),
                icon: 'error'
            })
        } finally {
            setLoading(false)
        }
    }

    useEffect(() => {
        void getData('0')
        // Data pertama hanya dimuat saat halaman dibuka.
    }, [])

    const handleDelete = async (row: any) => {
        const rowId = String(row?.id || '')

        if (!rowId) return

        const confirmation = await Swal.fire({
            title: 'Hapus dokumen BHP?',
            text: `Dokumen ${rowId} akan dihapus. Tindakan ini tidak dapat dibatalkan.`,
            icon: 'warning',
            showCancelButton: true,
            confirmButtonText: 'Ya, Hapus',
            cancelButtonText: 'Batal',
            confirmButtonColor: '#d32f2f'
        })

        if (!confirmation.isConfirmed) return

        setDeletingId(rowId)

        try {
            const response = await axios.delete(
                `${apiUrl.apiUrl}/production/bhp/delete/${encodeURIComponent(
                    rowId
                )}`,
                {
                    headers: {
                        Accept: 'application/json',
                        Authorization: `Bearer ${token}`
                    }
                }
            )

            if (!response.data?.success) {
                throw new Error(
                    response.data?.message ||
                    'Dokumen BHP gagal dihapus.'
                )
            }

            await Swal.fire({
                title: 'Berhasil',
                text:
                    response.data?.message ||
                    'Dokumen BHP berhasil dihapus.',
                icon: 'success',
                timer: 1200,
                showConfirmButton: false
            })

            await getData(tabs)
        } catch (error) {
            await Swal.fire({
                title: 'Pemberitahuan',
                text: getErrorMessage(
                    error,
                    'Dokumen BHP gagal dihapus.'
                ),
                icon: 'error'
            })
        } finally {
            setDeletingId('')
        }
    }

    const handlePrintPdf = async (row: any) => {
        const rowId = String(row?.id || '')

        if (!rowId || printingId) return

        /*
         * Window dibuka sebelum request agar tidak diblokir popup blocker.
         * Setelah PDF selesai diambil menggunakan Bearer token, tab ini
         * diarahkan ke blob PDF untuk preview dan print dari browser.
         */
        const previewWindow = window.open('', '_blank')

        if (!previewWindow) {
            await Swal.fire({
                title: 'Popup diblokir',
                text: 'Izinkan popup pada browser agar preview PDF dapat dibuka.',
                icon: 'warning'
            })

            return
        }

        previewWindow.document.write(`
      <!doctype html>
      <html>
        <head>
          <title>Menyiapkan PDF BHP</title>
          <style>
            body {
              margin: 0;
              min-height: 100vh;
              display: grid;
              place-items: center;
              font-family: Arial, sans-serif;
              color: #334155;
              background: #f8fafc;
            }
            .box { text-align: center; }
            .loader {
              width: 34px;
              height: 34px;
              margin: 0 auto 14px;
              border: 4px solid #dbeafe;
              border-top-color: #2563eb;
              border-radius: 50%;
              animation: spin .8s linear infinite;
            }
            @keyframes spin { to { transform: rotate(360deg); } }
          </style>
        </head>
        <body>
          <div class="box">
            <div class="loader"></div>
            <strong>Menyiapkan PDF BHP...</strong>
          </div>
        </body>
      </html>
    `)
        previewWindow.document.close()

        setPrintingId(rowId)

        try {
            const url = `${apiUrl.baseUrl}/production/bhp/pdf/${encodeURIComponent(
                rowId
            )}`

            const response = await axios.get<Blob>(url, {
                headers: {
                    Accept: 'application/pdf',
                    Authorization: `Bearer ${token}`
                },
                responseType: 'blob'
            })

            const contentType = String(
                response.headers?.['content-type'] ||
                response.data?.type ||
                ''
            ).toLowerCase()

            if (!contentType.includes('application/pdf')) {
                throw new Error(
                    await readBlobError(
                        response.data,
                        'Server tidak mengembalikan file PDF.'
                    )
                )
            }

            const pdfBlob = new Blob([response.data], {
                type: 'application/pdf'
            })
            const objectUrl = URL.createObjectURL(pdfBlob)

            previewWindow.location.replace(objectUrl)

            // Cukup panjang agar PDF viewer selesai membaca blob.
            window.setTimeout(() => {
                URL.revokeObjectURL(objectUrl)
            }, 5 * 60 * 1000)
        } catch (error) {
            previewWindow.close()

            let message = getErrorMessage(
                error,
                'Gagal membuat PDF BHP.'
            )

            if (
                axios.isAxiosError(error) &&
                error.response?.data instanceof Blob
            ) {
                message = await readBlobError(
                    error.response.data,
                    message
                )
            }

            await Swal.fire({
                title: 'PDF gagal dibuat',
                text: message,
                icon: 'error'
            })
        } finally {
            setPrintingId('')
        }
    }

    const handleChangeTabs = (
        _event: SyntheticEvent,
        newValue: string
    ) => {
        setTabs(newValue)
        void getData(newValue)
    }

    const handleResetFilter = () => {
        setTglAwal(defaultTanggalAwal)
        setTglAkhir(defaultTanggalAkhir)
    }

    const ActionButtons = ({ row }: { row: any }) => {
        const rowId = String(row?.id || '')
        const isDeleting = deletingId === rowId
        const isPrinting = printingId === rowId

        return (
            <Stack
                direction='row'
                spacing={0.5}
                alignItems='center'
                justifyContent='center'
                sx={{ width: '100%' }}
            >
                <Tooltip title='Lihat Detail' placement='top'>
                    <IconButton
                        size='small'
                        color='primary'
                        onClick={() =>
                            navigate(`/production/bhp/detail/${rowId}`)
                        }
                    >
                        <Icon icon='mdi:eye-outline' fontSize={21} />
                    </IconButton>
                </Tooltip>

                <Tooltip title='Cetak PDF' placement='top'>
                    <span>
                        <IconButton
                            size='small'
                            color='success'
                            // disabled={Boolean(printingId)}
                            onClick={() => {
                                window.open(
                                    `${apiUrl.baseUrl}/production/bhp/pdf/${row.id}`,
                                    '_blank'
                                )
                            }}
                        >
                            {isPrinting ? (
                                <CircularProgress size={18} color='inherit' />
                            ) : (
                                <Icon
                                    icon='mdi:printer-outline'
                                    fontSize={21}
                                />
                            )}
                        </IconButton>
                    </span>
                </Tooltip>

                <Tooltip title='Hapus BHP' placement='top'>
                    <span>
                        <IconButton
                            size='small'
                            color='error'
                            disabled={Boolean(deletingId)}
                            onClick={() => void handleDelete(row)}
                        >
                            {isDeleting ? (
                                <CircularProgress size={18} color='inherit' />
                            ) : (
                                <Icon
                                    icon='mdi:delete-outline'
                                    fontSize={21}
                                />
                            )}
                        </IconButton>
                    </span>
                </Tooltip>
            </Stack>
        )
    }

    const ActionButtonsOutstanding = ({ row }: { row: any }) => (
        <Box
            sx={{
                width: '100%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
            }}
        >
            <Button
                variant='contained'
                color='success'
                size='small'
                startIcon={<Icon icon='mdi:file-document-plus-outline' />}
                onClick={async () => {
                    const confirmation = await Swal.fire({
                        title: 'Buat BHP?',
                        text: 'BHP akan dibuat dari Work Order ini.',
                        icon: 'question',
                        showCancelButton: true,
                        confirmButtonText: 'Ya, Buat BHP',
                        cancelButtonText: 'Batal'
                    })

                    if (confirmation.isConfirmed) {
                        navigate(`/production/bhp/add-wo/${row.id}`)
                    }
                }}
                sx={{
                    minWidth: 126,
                    fontWeight: 700,
                    boxShadow: 'none',
                    textTransform: 'none'
                }}
            >
                Buat BHP
            </Button>
        </Box>
    )

    const actionData = [
        {
            key: 'aksi',
            flex: 1,
            sortable: false,
            minWidth: 140,
            field: 'aksi',
            align: 'center',
            headerAlign: 'center',
            headerName: 'Aksi',
            renderCell: ({ row }: { row: any }) => (
                <ActionButtons row={row} />
            )
        }
    ]

    const actionOutstanding = [
        {
            key: 'aksi',
            flex: 1,
            sortable: false,
            minWidth: 165,
            field: 'aksi',
            align: 'center',
            headerAlign: 'center',
            headerName: 'Aksi',
            renderCell: ({ row }: { row: any }) => (
                <ActionButtonsOutstanding row={row} />
            )
        }
    ]

    const renderDataGrid = (actions: any[]) => {
        if (loading) {
            return (
                <Box
                    sx={{
                        minHeight: 280,
                        display: 'grid',
                        placeItems: 'center'
                    }}
                >
                    <Stack spacing={2} alignItems='center'>
                        <CircularProgress size={34} />
                        <Typography color='text.secondary'>
                            Mengambil data BHP...
                        </Typography>
                    </Stack>
                </Box>
            )
        }

        return <MsiDatagrid array={listData} actions={actions} />
    }

    return (
        <Grid
            container
            spacing={3}
            sx={{
                display: 'flex',
                flexWrap: 'wrap',
                justifyContent: 'center'
            }}
        >
            <Grid item xs={12}>
                <Card
                    className='content-block dx-card'
                    sx={{
                        overflow: 'hidden',
                        borderRadius: 2,
                        boxShadow: 'none',
                        border: theme =>
                            `1px solid ${theme.palette.divider}`
                    }}
                >
                    <Box
                        sx={{
                            px: { xs: 3, sm: 4 },
                            py: 3,
                            display: 'flex',
                            gap: 3,
                            flexWrap: 'wrap',
                            alignItems: 'center',
                            justifyContent: 'space-between',
                            color: 'primary.contrastText',
                            bgcolor: 'primary.main'
                        }}
                    >
                        <Box>
                            <Typography
                                variant='h5'
                                sx={{ fontWeight: 800, letterSpacing: 0.2 }}
                            >
                                BHP
                            </Typography>
                            <Typography
                                variant='body2'
                                sx={{ mt: 0.5, opacity: 0.86 }}
                            >
                                Kelola dokumen BHP, Work Order, material, biaya,
                                dan cetakan PDF.
                            </Typography>
                        </Box>

                        <Tooltip title='Tambah BHP'>
                            <Fab
                                size='medium'
                                onClick={() => navigate('/production/bhp/add')}
                                sx={{
                                    color: 'primary.main',
                                    bgcolor: 'common.white',
                                    boxShadow: 'none',
                                    '&:hover': {
                                        bgcolor: 'grey.100'
                                    }
                                }}
                            >
                                <Icon icon='mdi:plus' fontSize={30} />
                            </Fab>
                        </Tooltip>
                    </Box>

                    <CardContent sx={{ p: { xs: 3, sm: 4 } }}>
                        <Box
                            sx={{
                                p: { xs: 2.5, sm: 3 },
                                borderRadius: 2,
                                bgcolor: 'action.hover',
                                border: theme =>
                                    `1px solid ${theme.palette.divider}`
                            }}
                        >
                            <Typography
                                variant='subtitle1'
                                sx={{ fontWeight: 700, mb: 0.5 }}
                            >
                                Filter Periode
                            </Typography>
                            <Typography
                                variant='body2'
                                color='text.secondary'
                                sx={{ mb: 3 }}
                            >
                                Tentukan rentang tanggal, lalu tekan Cari Data.
                            </Typography>

                            <Grid container spacing={2.5} alignItems='center'>
                                <Grid item xs={12} md={3}>
                                    <FormControl fullWidth>
                                        <TextField
                                            fullWidth
                                            size='small'
                                            label='Tanggal Awal'
                                            type='date'
                                            value={tglAwal}
                                            onChange={event =>
                                                setTglAwal(event.target.value)
                                            }
                                            InputLabelProps={{ shrink: true }}
                                        />
                                    </FormControl>
                                </Grid>

                                <Grid item xs={12} md={3}>
                                    <FormControl fullWidth>
                                        <TextField
                                            fullWidth
                                            size='small'
                                            label='Tanggal Akhir'
                                            type='date'
                                            value={tglAkhir}
                                            onChange={event =>
                                                setTglAkhir(event.target.value)
                                            }
                                            InputLabelProps={{ shrink: true }}
                                        />
                                    </FormControl>
                                </Grid>

                                <Grid item xs={12} md='auto'>
                                    <Stack
                                        direction={{ xs: 'column', sm: 'row' }}
                                        spacing={1.5}
                                    >
                                        <Button
                                            variant='contained'
                                            startIcon={<Icon icon='mdi:magnify' />}
                                            disabled={loading}
                                            onClick={() => void getData(tabs)}
                                            sx={{
                                                minWidth: 135,
                                                minHeight: 40,
                                                fontWeight: 700,
                                                boxShadow: 'none',
                                                textTransform: 'none'
                                            }}
                                        >
                                            Cari Data
                                        </Button>

                                        <Button
                                            variant='outlined'
                                            color='secondary'
                                            startIcon={<Icon icon='mdi:filter-off-outline' />}
                                            disabled={loading}
                                            onClick={handleResetFilter}
                                            sx={{
                                                minWidth: 126,
                                                minHeight: 40,
                                                fontWeight: 700,
                                                textTransform: 'none'
                                            }}
                                        >
                                            Reset
                                        </Button>
                                    </Stack>
                                </Grid>
                            </Grid>
                        </Box>
                    </CardContent>

                    <Divider />

                    <TabContext value={tabs}>
                        <Box sx={{ px: { xs: 2, sm: 3 }, pt: 1 }}>
                            <TabList
                                scrollButtons='auto'
                                variant='scrollable'
                                onChange={handleChangeTabs}
                                aria-label='Tab daftar BHP'
                                sx={{
                                    '& .MuiTab-root': {
                                        minHeight: 54,
                                        fontWeight: 700,
                                        textTransform: 'none'
                                    }
                                }}
                            >
                                <Tab value='0' label='Daftar BHP' />
                                <Tab value='1' label='BHP Belum Aktif' />
                                <Tab value='2' label='Outstanding Work Order' />
                            </TabList>
                        </Box>

                        <Divider />

                        <TabPanel value='0' sx={{ p: { xs: 2, sm: 3 } }}>
                            {renderDataGrid(actionData)}
                        </TabPanel>

                        <TabPanel value='1' sx={{ p: { xs: 2, sm: 3 } }}>
                            {renderDataGrid(actionData)}
                        </TabPanel>

                        <TabPanel value='2' sx={{ p: { xs: 2, sm: 3 } }}>
                            {renderDataGrid(actionOutstanding)}
                        </TabPanel>
                    </TabContext>
                </Card>
            </Grid>
        </Grid>
    )
}
